import { Box, VStack, useToast } from "@chakra-ui/react";
import moment from "moment";
import { useMemo } from "react";
import * as XLSX from "xlsx";

import {
  EditGPDAddReportTask,
  GPDAddReportTask,
  GPDVolumeSeriesData,
  GpdMethodOptions,
  gpdMethodOptions,
} from "@nens/brostar/uploadapi-client";
import { useCompareEffect } from "@nens/react/hooks";

import { getApi } from "@/api/api-configuration";
import { getFormObjectValues, initializeFormEditingState } from "@/helpers/helpers";
import { parseNumber } from "@/helpers/parse-number";
import { useAfterFormSubmit } from "@/hooks/use-after-form-submit";
import { BrostarForm, useBrostarFormDataStorage } from "@/libs/brostar-form";
import { GpdAddReportFormProps } from "@/types/form-types";

import { useGpdUiSchema } from "../../schema/gpd-ui-schema";

import { GpdVolumeSeriesChart } from "./gpd-volume-series-chart";
import { getGpdAddReportSchema } from "./schema/gpd-add-report-schema";

function normalizeDate(value: unknown): string {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return moment(value).format("YYYY-MM-DD");
  }
  if (typeof value === "number") {
    return XLSX.SSF.format("yyyy-mm-dd", value);
  }
  if (typeof value === "string") {
    return value.trim();
  }
  return "";
}

function normalizeExcelRow(row: Record<string, unknown>): GPDVolumeSeriesData {
  // If row.temperatuur not in options, make it undefined
  const temperatureRaw = String(row.temperatuur ?? row.temperature ?? "").trim();
  const temperature = temperatureRaw
    ? (temperatureRaw as GPDVolumeSeriesData["temperature"])
    : undefined;

  const volumeRaw = String(row.volume ?? "").trim();

  return {
    waterInOut: String(
      row.onttrokken_ingebracht ?? row.water_in_out ?? row.waterInOut ?? ""
    ).trim() as GPDVolumeSeriesData["waterInOut"],
    volume: parseNumber(volumeRaw) ?? undefined, // If volumeRaw is an empty string, set volume to undefined
    temperature,
    beginDate: normalizeDate(row.begin_datum ?? row.beginDate),
    endDate: normalizeDate(row.eind_datum ?? row.endDate),
  };
}

export function GpdAddReportForm(props: GpdAddReportFormProps) {
  const api = getApi();
  const afterSubmit = useAfterFormSubmit();
  const toast = useToast();
  const { type, oldUploadTask, task } = getFormObjectValues(props);

  const { isEditing, getIsEditing } = initializeFormEditingState(props);

  const { formData, handleChange } = useBrostarFormDataStorage<
    GPDAddReportTask | EditGPDAddReportTask
  >({ key: "gpdAddReport", task });
  const uiSchema = useGpdUiSchema(formData?.metadata.qualityRegime);
  const schema = useMemo(() => getGpdAddReportSchema({ type, isEditing }), [type, isEditing]);

  useCompareEffect(() => {
    const file = formData?.volumeFile;
    if (!file || !(file instanceof Blob) || file.size === 0) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const workbook = XLSX.read(event.target?.result, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const excelRows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: "" });

        const parsedRows = excelRows
          .map(normalizeExcelRow)
          .filter(
            (row) =>
              row.beginDate ||
              row.endDate ||
              row.temperature ||
              row.waterInOut ||
              Number.isFinite(row.volume)
          );

        if (!formData) return;

        const firstRow = excelRows[0] ?? {};
        const reportId = String(
          firstRow.rapport_id ?? firstRow.report_id ?? formData.sourcedocumentData.reportId ?? ""
        ).trim();
        const groundwaterUsageFacilityBroId = String(
          firstRow.guf_id ??
            firstRow.groundwater_usage_facility_bro_id ??
            formData.sourcedocumentData.groundwaterUsageFacilityBroId ??
            ""
        ).trim();
        const methodFromFile = String(firstRow.methode ?? firstRow.method ?? "").trim();
        const method =
          gpdMethodOptions.some((option) => option.value === methodFromFile) && methodFromFile
            ? (methodFromFile as GpdMethodOptions)
            : formData.sourcedocumentData.method;

        handleChange({
          formData: {
            ...formData,
            sourcedocumentData: {
              ...formData.sourcedocumentData,
              reportId,
              method,
              groundwaterUsageFacilityBroId,
              volumeSeries: parsedRows,
            },
          },
        });
      } catch (error) {
        toast({
          title: "Rapportbestand kon niet worden ingelezen",
          description: "Controleer of het bestand het GPD-template volgt.",
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      }
    };

    reader.readAsBinaryString(file as File);
  }, [formData?.volumeFile]);

  const onSubmit = async () => {
    if (!formData) return;

    if (getIsEditing(formData)) {
      await api.editGpdAddReport(formData as EditGPDAddReportTask);
    } else {
      await api.createGpdAddReport(formData);
    }
    if (oldUploadTask?.uuid) api.apiUploadtasksDelete({ uuid: oldUploadTask.uuid });
    await afterSubmit();
  };

  return (
    // need a small bottom margin to prevent the chart from being cut off at the bottom
    <VStack align="stretch" gap={0}>
      <BrostarForm
        schema={schema}
        uiSchema={uiSchema}
        formData={formData}
        onChange={handleChange}
        onSubmit={onSubmit}
        uploadTask={oldUploadTask}
      />
      <GpdVolumeSeriesChart volumeSeries={formData?.sourcedocumentData.volumeSeries ?? []} />
      <Box height="25px" />
    </VStack>
  );
}
